/**
 * Research plan preview (web): what a research task reads, what it makes, the
 * image, where it goes and the estimated credits of one run. Rendered inside
 * the builder's plan preview; the rows come from the shared
 * `reportPreviewRows` so web and native read the same.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { NovaReportPreview as NovaReportPreviewData } from '@tabbio/types';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { reportPreviewRows, type NovaReportPreviewRowId } from './nova-report-preview';

const ROW_ICONS: Record<NovaReportPreviewRowId, string> = {
  sources: 'travel_explore',
  result: 'description',
  image: 'image',
  delivery: 'send',
  credits: 'toll',
};

export function NovaReportPreview({ report }: { report: NovaReportPreviewData }) {
  const { t } = useTranslation('common');
  const rows = reportPreviewRows(t, report);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={sectionLabel}>{t('nova.report.title')}</span>
      <div style={card}>
        {rows.map((row, index) => (
          <div
            key={row.id}
            style={{
              ...rowStyle,
              borderTop: index === 0 ? 'none' : '1px solid var(--input-border)',
            }}
          >
            <MaterialSymbol
              name={ROW_ICONS[row.id]}
              size={16}
              style={{ color: 'var(--text-tertiary)', flexShrink: 0, marginTop: 2 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={rowLabel}>{row.label}</div>
              {row.values.map((value, valueIndex) => (
                <div key={`${row.id}-${valueIndex}`} style={rowValue} dir="auto">
                  {value}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const sectionLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

const card: CSSProperties = {
  borderRadius: 12,
  background: 'rgba(0,0,0,0.04)',
  display: 'flex',
  flexDirection: 'column',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
};

const rowLabel: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-tertiary)',
};

const rowValue: CSSProperties = {
  fontSize: 14,
  lineHeight: 1.45,
  color: 'var(--text-primary)',
  overflowWrap: 'anywhere',
};
